import classNames from "classnames";
import { toast } from "react-toastify";
import { useAccountContext } from "@providers/AccountProvider";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@supabase/auth-helpers-react";
import {
    fetchIntegrationRequests,
    integrationRequestsQueryKey,
    requestIntegration,
} from "@common/lib/api/integrationRequests";
import PackagePlusIcon from "@icons/package-plus.svg?react";
import * as Sentry from "@sentry/react";
import { IntegrationProvider, IntegrationThirdParty, isKnownIntegrationName } from "./Integrations";
import DownloadIcon from "@icons/download-01.svg?react";
import ImportModal from "@/common/components/import-modal/ImportModal";

const HAPPYDOC_CHROME_EXTENSION_URL = "https://chromewebstore.google.com/search/happydoc";

const statuses = {
    Connected: "text-green-700 bg-green-50 ring-green-600/20",
    Pending: "text-gray-600 bg-gray-50 ring-gray-500/10",
    Error: "text-red-700 bg-red-50 ring-red-600/10",
};

/**
 * Cards component for integrations
 * @param integrations
 * @returns JSX.Element
 */
const Cards = ({ integration }: any) => {
    const { accountData, fileIntegrationEnabled } = useAccountContext();
    const session = useSession();
    const queryClient = useQueryClient();
    const [isRequesting, setIsRequesting] = useState(false);

    // Same key on every card, so React Query makes one request for the whole grid.
    const { data: requests } = useQuery({
        queryKey: integrationRequestsQueryKey(accountData?.id),
        queryFn: () => fetchIntegrationRequests(accountData?.id as string),
        enabled: !!accountData?.id,
    });
    const requestedAt = requests?.[integration?.name];

    /**
     * Handle request install
     * @param integrationName
     */
    const handleRequestInstall = async (integrationName: string) => {
        if (!session) {
            Sentry.captureMessage("Session not found in integrations/Card.tsx", {
                level: "error",
            });
            return;
        }

        // Only the PIMS cards can be requested. Not a security control (the endpoint is callable directly;
        // API-Server bounds and Slack-escapes the name and the integration_requests CHECK backs that up),
        // but it fails fast if a non-card name ever reaches this component.
        if (!isKnownIntegrationName(integrationName)) {
            Sentry.captureMessage("Integration request for an unknown PIMS name", {
                level: "error",
                extra: { integrationName },
            });
            toast.error("We couldn't send your request. Please try again.");
            return;
        }

        setIsRequesting(true);
        try {
            await requestIntegration(integrationName, session?.user?.email);
            await queryClient.invalidateQueries({ queryKey: integrationRequestsQueryKey(accountData?.id) });
            toast.success("Thanks for requesting. Reach out to us over Intercom or email so we can get you set up.");
        } catch (error) {
            Sentry.captureException(error);
            toast.error("We couldn't send your request. Please try again.");
        } finally {
            setIsRequesting(false);
        }
    };

    const handleNavigate = () => {
        window.open(HAPPYDOC_CHROME_EXTENSION_URL, "_blank");
    };

    const renderAction = () => {
        switch (true) {
            case integration?.disabled:
                return (
                    <span className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-500 ring-1 ring-inset ring-gray-300 whitespace-nowrap">
                        Coming Soon
                    </span>
                );
            case integration?.connected:
                return (
                    <div
                        className={`${statuses.Connected} ml-auto rounded-md px-4 py-2 text-sm font-medium ring-1 ring-inset`}
                    >
                        Connected
                    </div>
                );
            case integration?.provider === IntegrationProvider.EzyVet:
                return (
                    <button type="button" className="secondary-button" onClick={handleNavigate}>
                        <DownloadIcon className="h-5 w-5 text-gray-700" />
                        Download
                    </button>
                );
            case integration?.provider === IntegrationProvider.File:
                return <ImportModal disabled={!fileIntegrationEnabled()} useConfirmModal={true} />;
            case !!requestedAt:
                return (
                    <div className="flex flex-col items-end gap-1">
                        <button type="button" className="secondary-button" disabled>
                            Requested
                        </button>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                            Requested {new Date(requestedAt).toLocaleDateString()}
                        </span>
                    </div>
                );
            default:
                return (
                    <button
                        type="button"
                        className="secondary-button"
                        disabled={isRequesting}
                        onClick={() => handleRequestInstall(integration.name)}
                    >
                        <PackagePlusIcon className="h-5 w-5 text-gray-700" />
                        Request
                    </button>
                );
        }
    };

    const renderDetails = () => {
        if (!integration?.disabled && integration?.connected) {
            switch (integration?.provider) {
                case IntegrationThirdParty.AVImark:
                case IntegrationThirdParty.Cornerstone:
                case IntegrationThirdParty.ImproMed:
                    return (
                        <dl className="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm leading-6 bg-white">
                            <dt className="text-gray-500">Last Request</dt>
                            <dd className="text-gray-700 text-[10px]">
                                <time dateTime={integration?.lastRequest}>
                                    {integration?.lastRequest
                                        ? new Date(integration?.lastRequest).toLocaleString()
                                        : "-"}
                                </time>
                            </dd>
                            <dt className="text-gray-500">Practice ID</dt>
                            <dd className="text-gray-700 text-[10px]">{integration?.practiceId || "-"}</dd>
                        </dl>
                    );
                case IntegrationProvider.VetSpire:
                case IntegrationProvider.Pulse:
                    return (
                        <dl className="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm leading-6 bg-white">
                            <dt className="text-gray-500">Last Request</dt>
                            <dd className="text-gray-700 text-[10px]">
                                <time dateTime={integration?.lastRequest}>
                                    {integration?.lastRequest
                                        ? new Date(integration?.lastRequest).toLocaleString()
                                        : "-"}
                                </time>
                            </dd>
                            <dt className="text-gray-500">API URL</dt>
                            <dd className="text-gray-700 text-[10px]">{integration?.apiUrl || "-"}</dd>
                        </dl>
                    );
                default:
                    return null;
            }
        }
        return null;
    };

    return (
        <div key={integration.id}>
            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-x-4 bg-white justify-between p-6 gap-5">
                    <div className="flex items-center gap-4">
                        <img
                            src={integration.imageUrl}
                            alt={integration.name}
                            className={classNames(
                                integration?.disabled && "opacity-50",
                                "h-12 w-12 flex-none rounded-lg bg-white object-cover ring-1 ring-gray-300",
                            )}
                        />
                        <div
                            className={classNames(
                                integration?.disabled ? "text-gray-400" : "text-gray-900",
                                "text-md font-semibold leading-6",
                            )}
                        >
                            {integration.name}
                            {/* DaySmart is read-only: we pull its appointments but cannot write documents back (EN-1026). */}
                            {integration?.provider === IntegrationProvider.DaySmart && (
                                <p className="text-xs font-normal text-gray-500">Read only, no export</p>
                            )}
                        </div>
                    </div>

                    <div>{renderAction()}</div>
                </div>
                {renderDetails()}
            </div>
        </div>
    );
};

export default Cards;
